<template>
    <li
        :class="[
            'nav-item',
            'sidebar-group',
            { open: expanded, active: hasActiveChild },
        ]"
    >
        <a class="nav-link group-toggle" @click="handleToggle">
            <i v-if="icon" :class="icon"></i>
            <p>{{ name }}</p>
            <span
                class="caret fa fa-chevron-up"
                :class="{ rotated: !expanded }"
            ></span>
        </a>
        <ul class="nav sub-nav" v-if="expanded">
            <sidebar-link
                v-for="(c, i) in children"
                :key="i"
                :to="c.path"
                :name="c.name"
                :icon="c.icon"
            />
        </ul>
    </li>
</template>

<script>
import { ref, computed, watch, inject, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

export default {
    name: 'SidebarGroup',
    props: {
        name: { type: String, required: true },
        icon: String,
        children: { type: Array, default: () => [] },
        basePath: { type: String, default: '' },
    },
    setup(props) {
        const route = useRoute();
        const store = useStore();
        const recalcArrow = inject('recalcArrow', () => {});
        const groupId = computed(() => props.basePath || props.name);

        const hasActiveChild = computed(
            () =>
                props.children.some(c => c.path === route.path) ||
                (props.basePath && props.basePath === route.path)
        );

        // 初始展開狀態：若有 active child 則展開；否則取 vuex 記錄
        const expanded = ref(
            hasActiveChild.value ||
                store.getters['sidebar/isGroupExpanded'](groupId.value)
        );

        const persist = () => {
            store.dispatch('sidebar/setGroupExpanded', {
                id: groupId.value,
                expanded: expanded.value,
            });
        };

        const handleToggle = () => {
            expanded.value = !expanded.value;
            persist();
            nextTick(() => recalcArrow());
        };

        // 路由變更：若命中並尚未展開則展開並記錄
        watch(
            () => route.path,
            () => {
                if (hasActiveChild.value && !expanded.value) {
                    expanded.value = true;
                    persist();
                    nextTick(() => recalcArrow());
                }
            },
            { immediate: true }
        );

        // 若 store 的展開狀態外部被改動 (未來擴充) 可同步
        watch(
            () => store.getters['sidebar/isGroupExpanded'](groupId.value),
            val => {
                // 避免覆蓋因路由強制展開的情況（已 active 就保持展開）
                if (!hasActiveChild.value) {
                    expanded.value = val;
                    nextTick(() => recalcArrow());
                }
            }
        );

        return { expanded, handleToggle, hasActiveChild };
    },
};
</script>

<style scoped>
.sidebar-group > .group-toggle {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 25px;
}
.sidebar-group > .group-toggle > p {
    font-weight: 500;
}
.sidebar-group .caret {
    transition: transform 0.25s;
}
.sidebar-group .caret.rotated {
    transform: rotate(-180deg);
}
.sub-nav {
    padding-left: 25px;
}
.sub-nav p {
    font-size: 11px;
}
.sidebar-group.active > .group-toggle {
    font-weight: 600;
}
</style>
